import { trigger, transition, style, animate } from '@angular/animations';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  forwardRef,
  signal,
  computed,
  HostListener,
  ElementRef,
  inject,
} from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';

import { VOneIconComponent } from '@shared-components';
import { IconType } from '@shared-enums';
import { DropdownItem } from '@shared-models';

/**
 * Three-dot menu dropdown component for card actions
 * Responsive component with pressed state and dynamic options
 *
 * @example
 * <v-one-menu-dropdown
 *   [options]="menuOptions"
 *   (optionSelected)="handleMenuSelect($event)">
 * </v-one-menu-dropdown>
 */
@Component({
  selector: 'v-one-menu-dropdown',
  imports: [forwardRef(() => VOneIconComponent), TranslatePipe],
  templateUrl: './v-one-menu-dropdown.component.html',
  styleUrl: './v-one-menu-dropdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('menuAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.95) translateY(-4px)' }),
        animate(
          '200ms ease-out',
          style({ opacity: 1, transform: 'scale(1) translateY(0)' }),
        ),
      ]),
      transition(':leave', [
        animate(
          '150ms ease-in',
          style({ opacity: 0, transform: 'scale(0.95) translateY(-4px)' }),
        ),
      ]),
    ]),
  ],
})
export class VOneMenuDropdownComponent {
  private readonly elementRef = inject(ElementRef);

  @Input() options: DropdownItem[] = [];
  @Input() disabled = false;
  @Input() menuPosition: 'left' | 'right' = 'right';

  @Output() optionSelected = new EventEmitter<string>();

  readonly isOpen = signal(false);
  readonly isPressed = signal(false);
  protected readonly ICON_TYPE = IconType;

  protected readonly iconColor = computed(() =>
    this.isPressed() ? 'var(--color-blue-400)' : 'var(--color-blue-700)',
  );

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeMenu();
    }
  }

  protected toggleMenu(): void {
    if (!this.disabled) {
      this.isOpen.update((state) => !state);
      this.isPressed.set(this.isOpen());
    }
  }

  protected closeMenu(): void {
    this.isOpen.set(false);
    this.isPressed.set(false);
  }

  protected selectOption(option: DropdownItem): void {
    if (option.disabled) return;

    this.optionSelected.emit(option.value);
    this.closeMenu();
  }

  protected onMenuKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeMenu();
    }
  }
}
